/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import { Hidden, Typography2, typography2 } from '@coursera/cds-core';

import usePrevious from 'bundles/common/hooks/usePrevious';
import ExpandableSearchFilterGroup from 'bundles/search-common/components/filters/ExpandableSearchFilterGroup';
import SearchFilterPlaceholder from 'bundles/search-common/components/filters/SearchFilterPlaceholder';
import SkipToResultsLink from 'bundles/search-common/components/filters/SkipToResultsLink';
import type { SearchFilterOption, SearchPageEventingData } from 'bundles/search-common/types';
import type { FacetConfig } from 'bundles/search-common/types/filter';

import _t from 'i18n!nls/search-common';

type Props = {
  eventingData: SearchPageEventingData;
  shouldShowSectionTitle: boolean;
  onFilterValueChange?: () => void;
  isMobile?: boolean;
  // Core data props - same as SearchFilters for consistency
  filters: Record<string, SearchFilterOption[] | undefined>;
  facets: FacetConfig[];
  facetFilters?: string[][]; // For loading state logic
};

const styles = {
  placeholderContainer: css`
    padding-top: 30px;
  `,
  filterTitle: css`
    ${typography2.subtitleLarge}
    margin-bottom: 32px;
  `,
  skipToResultsForNoTitle: css`
    position: absolute;
    top: -8px;
  `,
};

export const InlineSearchFilters = (props: Props): React.ReactElement | null => {
  const { eventingData, shouldShowSectionTitle, onFilterValueChange, isMobile, filters, facets, facetFilters } = props;

  const previousFacetFilters = usePrevious(facetFilters);

  // saving previous filters to prevent from showing placeholder and close modal
  // this will be refactored out in https://coursera.atlassian.net/browse/SEARCH-256
  const [previousFilters, setPreviousFilters] = useState(filters);
  useEffect(() => {
    if (filters && Object.keys(filters).length && previousFacetFilters?.length !== facetFilters?.length) {
      setPreviousFilters(filters);
    }
  }, [facetFilters?.length, filters, previousFacetFilters?.length]);

  const currentFilters = filters && Object.keys(filters).length ? filters : previousFilters;

  // Filters are loading if 'filters' (from Algolia) is undefined or is an empty object
  // and facets (static list of facets) is defined (which means we are expecting filter data from Algolia).
  const filtersLoading = !!facets && !Object.keys(currentFilters ?? {}).length;
  if (filtersLoading) {
    return (
      <div>
        {Array.from(Array(7)).map((_item, index) => {
          // eslint-disable-next-line react/no-array-index-key
          return <SearchFilterPlaceholder key={index} isCompact={true} />;
        })}
      </div>
    );
  }
  return (
    <Hidden smDown>
      <div css={!shouldShowSectionTitle && styles.skipToResultsForNoTitle}>
        <SkipToResultsLink isMobile={isMobile} />
      </div>
      {shouldShowSectionTitle && (
        <Typography2 component="h2" css={styles.filterTitle}>
          {_t('Filter by')}
        </Typography2>
      )}

      {facets
        .filter((group) => !!currentFilters?.[group.property]?.length)
        .map((group, i) => (
          <ExpandableSearchFilterGroup
            key={group.property}
            groupConfig={group}
            groupValues={currentFilters?.[group.property] ?? []}
            eventingData={{
              searchIndex: eventingData.searchIndex,
              searchIndexPosition: eventingData.searchIndexPosition,
              filtersApplied: eventingData.filtersApplied,
            }}
            onFilterValueChange={() => onFilterValueChange?.()}
            groupShouldHaveMinHeight={i === facets.length - 1}
          />
        ))}
    </Hidden>
  );
};

export default InlineSearchFilters;
